<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="my-commitments-page__title m-0">
          {{ $t('UserProjects.myCommitments.pageTitle') }}
        </h1>
        <p v-if="selectedProject?.name" class="my-commitments-page__subline m-0">
          {{ selectedProject.name }}
        </p>
      </div>
      <Button
        v-if="userProjectContextReady && !loading"
        :label="$t('UserProjects.myCommitments.createShort')"
        icon="pi pi-plus"
        class="btn-primary-accent shrink-0"
        unstyled
        data-testid="my-commitments-create"
        @click="openCreateDialog"
      />
    </div>

    <Message v-if="!hasProjectContext && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div
      v-if="hasProjectContext && userProjectContextReady && !loading"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <div class="flex flex-wrap items-center gap-2" data-testid="my-commitments-status-filters">
        <button
          v-for="option in statusFilterOptions"
          :key="option.value"
          type="button"
          class="my-commitments-page__pill"
          :class="{ 'my-commitments-page__pill--active': statusFilter === option.value }"
          :data-testid="`my-commitments-filter-${option.value}`"
          @click="setStatusFilter(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-3 ml-auto">
        <Select
          v-model="zeitraumFilter"
          :options="zeitraumOptions"
          option-label="label"
          option-value="value"
          class="my-commitments-page__select"
          data-testid="my-commitments-zeitraum-filter"
        />
        <Select
          v-model="milestoneFilter"
          :options="milestoneOptions"
          option-label="label"
          option-value="value"
          class="my-commitments-page__select"
          data-testid="my-commitments-milestone-filter"
        />
      </div>
    </div>

    <div v-if="loading || !userProjectContextReady" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.myCommitments.loading') }}
    </div>

    <div
      v-else-if="hasProjectContext && isEmpty"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ emptyTitle }}</div>
      <div class="text-sm mt-1">{{ emptyHint }}</div>
    </div>

    <div
      v-else-if="hasProjectContext && filteredCommitments.length === 0"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ $t('UserProjects.myCommitments.noMatchesTitle') }}</div>
      <div class="text-sm mt-1">{{ $t('UserProjects.myCommitments.noMatchesHint') }}</div>
    </div>

    <div
      v-else-if="hasProjectContext"
      class="flex flex-col gap-3"
      data-testid="my-commitments-list"
    >
      <CommitmentListCard
        v-for="commitment in filteredCommitments"
        :key="commitment.id"
        :commitment="commitment"
        @updated="refresh"
        @edit="openEditDialog"
        @delete="confirmDelete"
      />
    </div>

    <CommitmentFormDialog
      v-model:visible="dialogVisible"
      :mode="dialogMode"
      :project-id="selectedProjectId"
      :project-name="selectedProject?.name || ''"
      :initial-commitment="editingCommitment"
      :current-user-id="currentUserId"
      :default-owner-user-id="currentUserId"
      :participant-users="participantUsers"
      :saving="saving"
      @submit="submitCommitmentDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Select from 'primevue/select';
import CommitmentFormDialog from '@/components/CustomerApp/UserProjects/CommitmentFormDialog.vue';
import CommitmentListCard from '@/components/CustomerApp/UserProjects/CommitmentListCard.vue';
import { resolveCurrentUserId } from '@/utils/resolveCurrentUserId';
import {
  filterCommitmentsByMilestone,
  filterCommitmentsByZeitraum,
} from '@/utils/commitmentDateFilters';

export default {
  name: 'MyCommitmentsView',
  components: {
    Button,
    Message,
    Select,
    CommitmentFormDialog,
    CommitmentListCard,
  },
  inject: {
    participantUsersProvider: { from: 'participantUsers', default: () => () => [] },
  },
  setup() {
    return {
      confirm: useConfirm(),
      toast: useToast(),
    };
  },
  data() {
    return {
      dialogVisible: false,
      dialogMode: 'create',
      editingId: null,
      editingProjectId: null,
      editingCommitment: null,
      saving: false,
      statusFilter: 'open',
      zeitraumFilter: 'all',
      milestoneFilter: 'all',
    };
  },
  computed: {
    ...mapGetters('pmCommitment', {
      myCommitments: 'myCommitments',
      loading: 'myCommitmentsLoading',
      error: 'myCommitmentsError',
      isEmpty: 'myCommitmentsEmpty',
    }),
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      hasProjectContext: 'hasUserProjectContext',
      userProjectContextReady: 'userProjectContextReady',
    }),
    ...mapGetters('pmMilestone', {
      milestones: 'milestones',
    }),
    ...mapGetters(['getUser']),
    currentUserId() {
      return resolveCurrentUserId(this.getUser);
    },
    participantUsers() {
      return this.participantUsersProvider() || [];
    },
    statusFilterOptions() {
      return [
        { value: 'all', label: this.$t('UserProjects.myCommitments.filters.all') },
        { value: 'open', label: this.$t('UserProjects.myCommitments.filters.open') },
        { value: 'done', label: this.$t('UserProjects.myCommitments.filters.done') },
        { value: 'not_done', label: this.$t('UserProjects.myCommitments.filters.notDone') },
      ];
    },
    zeitraumOptions() {
      return [
        { value: 'all', label: this.$t('UserProjects.myCommitments.zeitraum.all') },
        { value: 'thisWeek', label: this.$t('UserProjects.myCommitments.zeitraum.thisWeek') },
        { value: 'thisMonth', label: this.$t('UserProjects.myCommitments.zeitraum.thisMonth') },
        { value: 'next30Days', label: this.$t('UserProjects.myCommitments.zeitraum.next30Days') },
      ];
    },
    milestoneOptions() {
      const options = [
        { value: 'all', label: this.$t('UserProjects.myCommitments.milestoneFilter.all') },
      ];
      (this.milestones || []).forEach((milestone) => {
        options.push({
          value: milestone.id,
          label: milestone.title,
        });
      });
      return options;
    },
    filteredCommitments() {
      let items = this.myCommitments || [];
      items = filterCommitmentsByZeitraum(items, this.zeitraumFilter);
      items = filterCommitmentsByMilestone(items, this.milestoneFilter);
      return items;
    },
    emptyTitle() {
      if (this.statusFilter === 'open') {
        return this.$t('UserProjects.myCommitments.emptyTitle');
      }
      return this.$t('UserProjects.myCommitments.emptyFilteredTitle');
    },
    emptyHint() {
      if (this.statusFilter === 'open') {
        return this.$t('UserProjects.myCommitments.emptyHint');
      }
      return this.$t('UserProjects.myCommitments.emptyFilteredHint');
    },
  },
  watch: {
    selectedProjectId() {
      this.milestoneFilter = 'all';
      this.refresh();
    },
    userProjectContextReady(isReady) {
      if (isReady) {
        this.refresh();
      }
    },
    statusFilter() {
      this.refresh();
    },
  },
  async mounted() {
    if (this.userProjectContextReady) {
      await this.refresh();
    }
    if (this.$route.query.create === '1' && this.hasProjectContext) {
      this.openCreateDialog();
      this.$router.replace({ path: this.$route.path, query: {} });
    }
  },
  methods: {
    ...mapActions('pmCommitment', {
      fetchMyCommitmentsForContext: 'fetchMyCommitmentsForContext',
      setCommitmentProjectContext: 'setProjectContext',
      createCommitment: 'createCommitment',
      updateCommitment: 'updateCommitment',
      deleteCommitment: 'deleteCommitment',
    }),
    ...mapActions('pmMilestone', {
      setMilestoneProjectContext: 'setProjectContext',
    }),

    setStatusFilter(value) {
      this.statusFilter = value;
    },

    async refresh() {
      if (!this.userProjectContextReady) return;
      try {
        if (this.selectedProjectId) {
          await this.setMilestoneProjectContext(this.selectedProjectId);
        }
        await this.fetchMyCommitmentsForContext({ status: this.statusFilter });
      } catch {
        // handled in store
      }
    },

    async withProjectContext(projectId, fn) {
      const contextId = projectId || this.selectedProjectId;
      await this.setCommitmentProjectContext(contextId);
      return fn();
    },

    openCreateDialog() {
      if (!this.hasProjectContext) {
        this.toast.add({
          severity: 'warn',
          summary: this.$t('UserProjects.myCommitments.noProjectsTitle'),
          detail: this.$t('UserProjects.context.required'),
          life: 4000,
        });
        return;
      }
      this.dialogMode = 'create';
      this.editingId = null;
      this.editingProjectId = null;
      this.editingCommitment = null;
      this.dialogVisible = true;
    },

    openEditDialog(commitment) {
      this.dialogMode = 'edit';
      this.editingId = commitment.id;
      this.editingProjectId = commitment.projectId;
      this.editingCommitment = commitment;
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
      this.saving = false;
      this.editingCommitment = null;
    },

    async submitCommitmentDialog(payload) {
      if (!this.currentUserId) {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.commitments.toast.error'),
          detail: this.$t('UserProjects.myCommitments.validation.userRequired'),
          life: 4000,
        });
        return;
      }
      this.saving = true;
      const projectId = this.dialogMode === 'create' ? this.selectedProjectId : this.editingProjectId;
      const apiPayload = {
        ...payload,
        ownerUserId: payload.ownerUserId || this.currentUserId,
      };
      try {
        await this.withProjectContext(projectId, async () => {
          if (this.dialogMode === 'create') {
            await this.createCommitment(apiPayload);
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.commitments.toast.created'),
              life: 3000,
            });
          } else {
            await this.updateCommitment({ id: this.editingId, payload: apiPayload });
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.commitments.toast.updated'),
              life: 3000,
            });
          }
        });
        this.closeDialog();
        await this.refresh();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.commitments.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(commitment) {
      this.confirm.require({
        message: this.$t('ProjectManagement.commitments.deleteConfirm', { name: commitment.was }),
        header: this.$t('ProjectManagement.commitments.deleteTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.withProjectContext(commitment.projectId, () => this.deleteCommitment(commitment.id));
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.commitments.toast.deleted'),
              life: 3000,
            });
            await this.refresh();
          } catch {
            this.toast.add({
              severity: 'error',
              summary: this.$t('ProjectManagement.commitments.toast.error'),
              life: 4000,
            });
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.my-commitments-page__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-dark);
}

.my-commitments-page__subline {
  margin-top: 0.125rem;
  font-size: 13px;
  color: var(--color-disabled);
}

.my-commitments-page__pill {
  padding: 0.375rem 0.875rem;
  border: none;
  border-radius: 999px;
  background: #f5f5f5;
  color: #6b6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.my-commitments-page__pill:hover {
  background: #eeeeee;
}

.my-commitments-page__pill--active {
  background: var(--color-orange);
  background: linear-gradient(135deg, var(--color-orange) 0%, #f59e0b 100%);
  color: #fff;
}

.my-commitments-page__select {
  min-width: 11rem;
}
</style>
